import { useState, useRef, useEffect } from 'react';
import { formatAudioTimestamp } from '../utils/AudioUtils';
import AudioVisualizer from './AudioVisualizer';

interface AudioControlsProps {
    src: string;
    type: string;
    onTimeUpdate?: (currentTime: number) => void;
}

export function AudioControls({ src, type, onTimeUpdate }: AudioControlsProps) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [playbackRate, setPlaybackRate] = useState(1);
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
            audioRef.current.playbackRate = playbackRate;
        }
    }, [volume, playbackRate]);

    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
            onTimeUpdate?.(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = parseFloat(e.target.value);
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        setVolume(value);
    };

    const handlePlaybackRateChange = (rate: number) => {
        setPlaybackRate(rate);
    };

    const skipTime = (seconds: number) => {
        if (audioRef.current) {
            const newTime = Math.max(0, Math.min(audioRef.current.currentTime + seconds, duration));
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    return (
        <div className="bg-surface rounded-lg p-4 sm:p-5 shadow">
            <audio
                ref={audioRef}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
            >
                <source src={src} type={type} />
            </audio>

            {/* Audio visualization */}
            <div className="mb-5 sm:mb-6">
                <AudioVisualizer
                    audioRef={audioRef}
                    isPlaying={isPlaying}
                    className="bg-gray-900 dark:bg-gray-800"
                />
            </div>

            {/* Time slider */}
            <div className="flex items-center gap-3 mb-5 sm:mb-6">
                <span className="text-sm text-text-secondary min-w-[3rem] text-right">
                    {formatAudioTimestamp(currentTime)}
                </span>
                <input
                    type="range"
                    min="0"
                    max={duration}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer dark:bg-gray-700 touch-manipulation"
                    style={{
                        '--range-thumb-size': '24px',
                        '--range-track-height': '8px'
                    } as React.CSSProperties}
                    aria-label="Audio progress"
                />
                <span className="text-sm text-text-secondary min-w-[3rem]">
                    {formatAudioTimestamp(duration)}
                </span>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-center gap-5 sm:justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                    {/* Skip backward */}
                    <button
                        onClick={() => skipTime(-10)}
                        className="btn-icon bg-gray-100 hover:bg-gray-200 active:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600 text-text dark:text-white tap-highlight-none touch-manipulation rounded-full"
                        aria-label="Skip backward 10 seconds"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
                        </svg>
                    </button>

                    {/* Play/Pause */}
                    <button
                        onClick={togglePlay}
                        className="btn-icon bg-primary hover:bg-primary-dark text-white tap-highlight-none touch-manipulation rounded-full transition-all duration-200 hover:animate-touch-press"
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                        {isPlaying ? (
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </button>

                    {/* Skip forward */}
                    <button
                        onClick={() => skipTime(10)}
                        className="btn-icon bg-gray-100 hover:bg-gray-200 active:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600 text-text dark:text-white tap-highlight-none touch-manipulation rounded-full"
                        aria-label="Skip forward 10 seconds"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                        </svg>
                    </button>
                </div>

                <div className="flex items-center gap-4 mt-3 sm:mt-0">
                    {/* Volume control - Hidden on mobile, visible on desktop */}
                    <div className="hidden sm:flex items-center gap-3">
                        <button
                            onClick={() => setVolume(volume === 0 ? 1 : 0)}
                            className="btn-icon bg-gray-100 hover:bg-gray-200 active:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600 text-text dark:text-white tap-highlight-none touch-manipulation rounded-full h-10 w-10"
                            aria-label={volume === 0 ? 'Unmute' : 'Mute'}
                        >
                            {volume === 0 ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                </svg>
                            )}
                        </button>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={volume}
                            onChange={handleVolumeChange}
                            className="w-24 h-2 bg-gray-200 rounded-full appearance-none cursor-pointer dark:bg-gray-700 touch-manipulation"
                            style={{
                                '--range-thumb-size': '20px',
                                '--range-track-height': '8px'
                            } as React.CSSProperties}
                            aria-label="Volume"
                        />
                    </div>

                    {/* Playback speed */}
                    <select
                        value={playbackRate}
                        onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
                        className="bg-gray-100 dark:bg-gray-700 text-text dark:text-white rounded-lg px-4 py-3 text-sm font-medium select-none tap-highlight-none touch-manipulation min-h-[var(--mobile-touch-target-size)] min-w-[80px]"
                        aria-label="Playback speed"
                    >
                        <option value="0.5">0.5x</option>
                        <option value="0.75">0.75x</option>
                        <option value="1">1x</option>
                        <option value="1.25">1.25x</option>
                        <option value="1.5">1.5x</option>
                        <option value="2">2x</option>
                    </select>
                </div>
            </div>
        </div>
    );
}
